import React from 'react';
import {
  Box,
  FormHelperText,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { deriveAnnualTotal } from './bandConfigurationUtils';

/**
 * Fixed three-row editor for generic BandDTO[].
 * Business bands: Band 1 (highest performance) → Band 3 (lowest performance).
 * Internal editor keys: highest = Band 1, middle = Band 2, lowest = Band 3.
 * Matching: Band 1 avg >= lower; Band 2 lower <= avg < upper; Band 3 avg < upper
 * (zero / missing hours → Band 3). Each band defines independent Q1–Q4 CL allocations.
 */
const BandConfigurationEditor = ({ value, onChange, errors }) => {
  const handleFieldChange = (role, field) => (event) => {
    const nextValue = event.target.value;
    onChange({
      ...value,
      [role]: {
        ...value[role],
        [field]: nextValue,
      },
    });
  };

  const quarterFields = [
    { name: 'q1Allocation', label: 'Q1 Casual Leaves', integer: true },
    { name: 'q2Allocation', label: 'Q2 Casual Leaves', integer: true },
    { name: 'q3Allocation', label: 'Q3 Casual Leaves', integer: true },
    { name: 'q4Allocation', label: 'Q4 Casual Leaves', integer: true },
  ];

  const renderBandCard = ({ title, meaning, role, limitFields }) => {
    const annualTotal = deriveAnnualTotal(value[role]);
    return (
      <Box
        sx={{
          border: '1px solid #e0e0e0',
          borderRadius: 1,
          p: 2,
          mb: 2,
          backgroundColor: '#fafafa',
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          {meaning}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Office Hours
        </Typography>
        <Grid container spacing={2} sx={{ mb: 1 }}>
          {limitFields.map((field) => (
            <Grid item xs={12} sm={6} md={4} key={`${role}-${field.name}`}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={field.label}
                name={`${role}-${field.name}`}
                value={value[role][field.name] ?? ''}
                onChange={handleFieldChange(role, field.name)}
                disabled={field.disabled}
                error={Boolean(errors?.[role]?.[field.name])}
                helperText={
                  field.disabled
                    ? field.disabledHelper || 'Not applicable'
                    : errors?.[role]?.[field.name] || ''
                }
                inputProps={{ min: 0, step: 0.1 }}
              />
            </Grid>
          ))}
        </Grid>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Quarterly Casual Leave Allocation
        </Typography>
        <Grid container spacing={2}>
          {quarterFields.map((field) => (
            <Grid item xs={6} sm={3} key={`${role}-${field.name}`}>
              <TextField
                fullWidth
                size="small"
                type="number"
                label={field.label}
                name={`${role}-${field.name}`}
                value={value[role][field.name] ?? ''}
                onChange={handleFieldChange(role, field.name)}
                error={Boolean(errors?.[role]?.[field.name])}
                helperText={errors?.[role]?.[field.name] || ''}
                inputProps={{ min: 0, step: 1 }}
              />
            </Grid>
          ))}
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              label="Annual Total"
              value={annualTotal == null ? '' : String(annualTotal)}
              disabled
              helperText="Derived from Q1 + Q2 + Q3 + Q4 (read-only)"
            />
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 1 }}>
        Quarterly Casual Leave Allocation
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Configure continuous office-hours ranges and Casual Leave days per quarter.
        Example: Band 1 averageHours ≥ 6, Band 2 6 &gt; averageHours ≥ 4.5, Band 3
        averageHours &lt; 4.5 (includes zero hours and missing prior-quarter data). Band 1
        is the most generous allocation; Band 3 is the least. Annual totals are derived
        per band and are not editable. There is no separate group-level annual or
        quarterly quota.
      </Typography>

      {renderBandCard({
        title: 'Band 1 — Highest Performance',
        meaning:
          'Most generous Casual Leave. Threshold shape: lower-limit only. Matches when averageOfficeHours ≥ lowerLimit (e.g. ≥ 6).',
        role: 'highest',
        limitFields: [
          { name: 'lowerLimit', label: 'Lower Limit (hours)' },
          {
            name: 'upperLimit',
            label: 'Upper Limit',
            disabled: true,
            disabledHelper: 'Always open-ended (null)',
          },
        ],
      })}

      {renderBandCard({
        title: 'Band 2 — Medium Performance',
        meaning:
          'Middle Casual Leave. Threshold shape: both limits. Matches when lowerLimit ≤ averageOfficeHours < upperLimit (e.g. 4.5 ≤ avg < 6). Band 2 upper must equal Band 1 lower.',
        role: 'middle',
        limitFields: [
          { name: 'lowerLimit', label: 'Lower Limit (hours)' },
          { name: 'upperLimit', label: 'Upper Limit (hours)' },
        ],
      })}

      {renderBandCard({
        title: 'Band 3 — Lowest Performance',
        meaning:
          'Least generous Casual Leave. Threshold shape: upper-limit only. Matches when averageOfficeHours < upperLimit (e.g. < 4.5), including 0 hours and non-calculable prior-quarter hours. Band 3 upper must equal Band 2 lower.',
        role: 'lowest',
        limitFields: [
          {
            name: 'lowerLimit',
            label: 'Lower Limit',
            disabled: true,
            disabledHelper: 'Always open-ended (null)',
          },
          { name: 'upperLimit', label: 'Upper Limit (hours)' },
        ],
      })}

      {errors?.general ? (
        <FormHelperText error sx={{ mt: 0, mb: 1 }}>
          {errors.general}
        </FormHelperText>
      ) : null}
    </Box>
  );
};

export default BandConfigurationEditor;
